import { Form, Input, Select } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { BaseReposLabel } from "@/pages/IntegrationsPage/components/molecules/BaseReposLabel"
import {
  getRepositoryProblem,
  normalizeRepository
} from "@/pages/IntegrationsPage/helpers/dockerHub"

import styles from "./FormDockerHub.module.scss"

import type { DockerHubFormValues } from "@/pages/IntegrationsPage/hooks/useDockerHubAPI"
import type { FormDockerHubProps } from "."

const SAVED_TOKEN = "••••••••••••••••"

const PREFIX = "integrations.dockerHub.form"

export const FormDockerHub = (props: FormDockerHubProps) => {
  const { form, formId, account, onFieldsChange, onFinish, t } = props
  const isNew = !account
  const { t: tForm } = useTranslation("translation", { keyPrefix: PREFIX })
  const required = { required: true, message: t("error.requiredField") }
  const initialValues: DockerHubFormValues = account
    ? { username: account.accountId, repositories: account.repositories }
    : { repositories: [] }

  return (
    <Form
      className={styles.form}
      form={form}
      id={formId}
      layout="vertical"
      initialValues={initialValues}
      onFieldsChange={onFieldsChange}
      onFinish={onFinish}
    >
      <Form.Item
        label={t(`${PREFIX}.username.label`)}
        name="username"
        required={false}
        rules={
          isNew
            ? [
                required,
                { whitespace: true, message: t("error.requiredField") }
              ]
            : []
        }
      >
        <Input
          ref={props.focusRef}
          autoComplete="off"
          disabled={!isNew}
          placeholder={t(`${PREFIX}.username.placeholder`)}
        />
      </Form.Item>

      <Form.Item
        label={t(`${PREFIX}.accessToken.label`)}
        name="accessToken"
        required={false}
        rules={
          isNew
            ? [
                required,
                { whitespace: true, message: t("error.requiredField") }
              ]
            : []
        }
      >
        <Input
          type="password"
          autoComplete="new-password"
          placeholder={
            isNew ? t(`${PREFIX}.accessToken.placeholder`) : SAVED_TOKEN
          }
        />
      </Form.Item>

      <Form.Item
        label={<BaseReposLabel t={tForm} />}
        name="repositories"
        required={false}
        normalize={(value: string[]) =>
          Array.from(new Set((value || []).map(normalizeRepository))).filter(
            Boolean
          )
        }
        rules={[
          {
            validator: (_, value: string[] = []) => {
              for (const repository of value) {
                const problem = getRepositoryProblem(repository)

                if (problem)
                  return Promise.reject(
                    new Error(
                      t(`${PREFIX}.repositories.${problem}`, { repository })
                    )
                  )
              }

              return Promise.resolve()
            }
          }
        ]}
      >
        <Select
          mode="tags"
          open={false}
          suffixIcon={null}
          tokenSeparators={[",", " "]}
          placeholder={tForm("baseReposInput.placeholder")}
        />
      </Form.Item>
    </Form>
  )
}
